import React, { useState } from 'react';
import Accordion from '../../common/Accordion';
import ContainerPermission from '../ContainerPermission';
import {
  createContainerRole,
  updateContainerRole,
  destroyContainerRole,
  destroyAllUserRoles,
} from '../constants/handleRoles';
import TemplatePermissionResult from './TemplatePermissionResult';
import ContainerFilterButtons from './ContainerFilterButtons';
import {
  applyTemplateToUser,
  generateTemplateFromUser,
} from '../constants/handleTemplates';
import { deleteUser } from '../constants/handleUsers';
import { notify } from '../../../helpers/notifications';
import Icon from '../../common/Icon';

const ManageRolesByUser = ({
  users,
  externals,
  containers,
  updateUsersState,
  templates,
  updateTemplateState,
  removeUserFromState,
}) => {
  const [selectedUser, setSelectedUser] = useState('');
  const [userAccordion, setUserAccordion] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [allowOverwrite, setAllowOverwrite] = useState(false);
  const [filter, setFilter] = useState('active');
  const [userID, modelName] = selectedUser
    ? selectedUser.split('-')
    : [null, null];

  const determineUserRoles = () => {
    if (modelName == 'user') {
      return users.find((u) => u.id == userID).roles;
    } else if (modelName == 'external_user') {
      return externals.find((e) => e.id == userID).roles;
    }
    return [];
  };

  const determineContainerDisplay = () => {
    let userRoleContainers = userRoles.map((r) => r.container_id);
    if (filter === 'all') {
      return containers;
    } else if (filter === 'active') {
      return containers.filter((c) => c.active);
    } else if (filter === 'roles') {
      return containers.filter((c) => userRoleContainers.includes(c.id));
    } else if (filter === 'template') {
      if (!templates.find((t) => t.id == selectedTemplate)) {
        // prevent bug when deleting template while it is selected in user dropdown
        setSelectedTemplate('');
        setFilter('all');
        return containers;
      }
      let templateRoleContainers = templates
        .find((t) => t.id == selectedTemplate)
        .template_roles.map((tr) => tr.container_id);

      let unifiedContainers = templateRoleContainers.concat(userRoleContainers);

      return containers.filter((c) => unifiedContainers.includes(c.id));
    }
    return containers;
  };

  const userRoles = determineUserRoles();
  const displayContainers = determineContainerDisplay();

  const handleUserSelect = (e) => {
    setSelectedUser(e.target.value);
  };

  const handleTemplateSelect = (value) => {
    setSelectedTemplate(value);
    setFilter(value ? 'template' : 'all');
  };

  const handleRemoveAll = async () => {
    await destroyAllUserRoles(userID, modelName, (response) => {
      if (response.ok) updateUsersState(userID, modelName, []);
    });
  };

  const handleDeleteUser = async () => {
    if (modelName === 'external_user') {
      return notify.alertFailure(
        'Notice',
        "Test Users can be managed by clicking 'Go to Test Users'"
      );
    } else if (modelName === 'user' && userID) {
      await deleteUser(userID, (response) => {
        if (response.ok) {
          setSelectedUser('');
          removeUserFromState(userID);
        }
      });
    }
  };

  const handleApplyTemplate = async () => {
    let result = await applyTemplateToUser(
      selectedTemplate,
      modelName,
      userID,
      allowOverwrite
    );
    if (result.ok) {
      updateUsersState(userID, modelName, result.data.roles);
      setSelectedTemplate('');
      setFilter('roles');
    }
  };

  const handleGenerateTemplate = async () => {
    let result = await generateTemplateFromUser(userID, modelName);
    if (result.ok) {
      updateTemplateState([...templates, result.data]);
      setSelectedTemplate(result.data.id.toString());
      setFilter('template');
    }
  };

  const handleCreateRole = async (container_id) => {
    let role = { [modelName + '_id']: userID, role: 0, container_id };
    let created = await createContainerRole(role);
    if (created.ok) {
      let roles = [...userRoles, created.data];
      updateUsersState(userID, modelName, roles);
    }
  };

  const handleUpdateRole = async (roleID, permission) => {
    let role = { role: permission };
    let updated = await updateContainerRole(roleID, role);
    if (updated.ok) {
      let roles = [...userRoles.filter((r) => r.id != roleID), updated.data];
      updateUsersState(userID, modelName, roles);
    }
  };

  const handleDestroyRole = async (roleID) => {
    let deleted = await destroyContainerRole(roleID);
    let roles = userRoles.filter((r) => r.id != roleID);
    if (deleted.ok) updateUsersState(userID, modelName, roles);
  };

  return (
    <section className='user-roles manage-users'>
      <h2>Manage Roles by User</h2>
      <Accordion
        expanded={userAccordion}
        toggle={() => setUserAccordion(!userAccordion)}
      >
        <div className='row'>
          <div className='third'>
            <label>Select User: </label>
            <div className='select-input'>
              <select
                value={selectedUser}
                onChange={(e) => handleUserSelect(e)}
              >
                <option value=''>Select User</option>
                <optgroup label='Users'>
                  {users.map((user) => (
                    <option
                      key={`user-${user.id}`}
                      value={`${user.id}-user`}
                      label={user.name}
                    />
                  ))}
                </optgroup>
                <optgroup label='Test'>
                  {externals.map((ex) => (
                    <option
                      key={`ex-${ex.id}`}
                      value={`${ex.id}-external_user`}
                      label={ex.name}
                    />
                  ))}
                </optgroup>
              </select>
            </div>
          </div>

          <div className='third'>
            <label>Select Template: </label>
            {selectedTemplate && (
              <div className='right'>
                <input
                  type='checkbox'
                  id='allow-overwrite'
                  checked={allowOverwrite}
                  onChange={(e) => setAllowOverwrite(!allowOverwrite)}
                />
                <label htmlFor='allow-overwrite'>Allow Overwrites</label>
              </div>
            )}
            <div className='select-input'>
              <select
                value={selectedTemplate}
                onChange={(e) => handleTemplateSelect(e.target.value)}
                disabled={!selectedUser}
              >
                <option value=''>Default (None)</option>
                <optgroup label='Templates'>
                  {templates.map((template) => (
                    <option
                      key={`apply-${template.id}`}
                      value={template.id}
                      label={template.name}
                    />
                  ))}
                </optgroup>
              </select>
            </div>
          </div>
          <div className='controls right' style={{ fontSize: '16px' }}>
            <button
              onClick={() => handleRemoveAll()}
              disabled={!selectedUser}
              className={selectedUser ? 'danger' : ''}
            >
              Remove All Roles <Icon icon='trash' />
            </button>
            <button
              onClick={() => handleDeleteUser()}
              disabled={!selectedUser}
              className={selectedUser ? 'danger' : ''}
            >
              Delete User <Icon icon='ban' />
            </button>
          </div>
        </div>

        <div className='row'>
          <div className='third'>
            <button
              onClick={() => handleGenerateTemplate()}
              disabled={!selectedUser}
            >
              Generate Template <Icon icon='folder-plus' />
            </button>
          </div>
        </div>
        <h3>
          User Permissions {filter == 'all' && '(All Containers)'}
          {filter == 'active' && '(Active Containers)'}
          {filter == 'roles' && '(Existing Roles)'}
          {filter == 'template' && '(Previewing Template Result)'}
        </h3>
        {filter === 'template' && (
          <div className='right'>
            Overwrite Conflicting Roles?{' '}
            <strong>{allowOverwrite ? 'Yes' : 'No'}</strong>
          </div>
        )}
        <div className='row'>
          <label className='name'>Viewing:</label>
          <div className='controls'>
            <ContainerFilterButtons
              disabled={selectedTemplate || !selectedUser}
              filter={filter}
              setFilter={setFilter}
            />
            <div className='right'>
              <button
                onClick={() => handleApplyTemplate()}
                disabled={!selectedTemplate || !selectedUser}
              >
                Apply Template
              </button>
            </div>
          </div>
        </div>
        {selectedUser && (
          <>
            <ul id='user-permissions'>
              {selectedTemplate &&
              templates.find((t) => t.id == selectedTemplate) ? (
                <TemplatePermissionResult
                  allContainers={displayContainers}
                  userRoles={userRoles}
                  templateRoles={
                    templates.find((t) => t.id == selectedTemplate)
                      .template_roles
                  }
                  allowOverwrite={allowOverwrite}
                />
              ) : (
                displayContainers.map((container) => (
                  <li
                    key={`container-${container.id}`}
                    className={container.active ? '' : 'inactive'}
                  >
                    {userRoles
                      .map((r) => r.container_id)
                      .includes(container.id) ? (
                      <div>
                        <div className='name'>{container.name}</div>
                        <ContainerPermission
                          role={userRoles.find(
                            (r) => r.container_id == container.id
                          )}
                          updateRole={handleUpdateRole}
                          destroyRole={handleDestroyRole}
                        />
                      </div>
                    ) : (
                      <div>
                        <div className='name'>{container.name}</div>
                        <button
                          className='add-container-btn'
                          onClick={(e) => handleCreateRole(container.id)}
                          title='Add user to container'
                        >
                          <Icon icon='plus' />
                        </button>
                      </div>
                    )}
                  </li>
                ))
              )}
            </ul>
          </>
        )}
      </Accordion>
    </section>
  );
};

export default ManageRolesByUser;
